import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

export interface ModuleCardBodyMetaLine {
	id: string;
	content: ReactNode;
	/** Leading icon. The line then wraps instead of clamping, with the icon pinned to the first line. */
	icon?: ReactNode;
}

export interface ModuleCardBodyProps {
	title: string;
	titleMinHeight?: string;
	/** Event name, rendered on its own row under the title rather than concatenated into it. */
	eventName?: string;
	subtitle?: ReactNode;
	subtitleMinHeight?: string;
	/** Drop the subtitle row when there is no subtitle instead of reserving its height. Opt-in, so existing callers keep the row that aligns them in a grid. */
	hideEmptySubtitle?: boolean;
	metaLines?: ModuleCardBodyMetaLine[];
	metaLineMinHeight?: string;
	footerLeft?: ReactNode;
	footerLeftIsPrice?: boolean;
	footerRight?: ReactNode;
	footerMinHeight?: string;
	/** Drop the footer row instead of reserving its height. Opt-in, so existing callers keep the row that aligns them in a grid. */
	hideBottomRow?: boolean;
	className?: string;
	variant?: 'light' | 'dark';
}

const DARK_TEXT_COLOR = '#E9EAEB';

export const ModuleCardBody = ({
	title,
	titleMinHeight = '40px',
	eventName,
	subtitle,
	subtitleMinHeight = '20px',
	hideEmptySubtitle = false,
	metaLines,
	metaLineMinHeight = '20px',
	footerLeft,
	footerLeftIsPrice = false,
	footerRight,
	footerMinHeight = '28px',
	hideBottomRow = false,
	className,
	variant = 'light'
}: ModuleCardBodyProps) => {
	const isDark = variant === 'dark';

	const darkTextStyle: CSSProperties | undefined = isDark ? { color: DARK_TEXT_COLOR } : undefined;
	const footerLeftStyle: CSSProperties | undefined = isDark ? { color: footerLeftIsPrice ? '#FFFFFF' : DARK_TEXT_COLOR } : undefined;

	return (
		<div className={clsx('flex flex-col', className)}>
			<div className="flex flex-col gap-2">
				<div
					className={clsx('!line-clamp-2 block text-sm font-medium', isDark ? '' : 'text-gray-900')}
					title={title}
					style={{ minHeight: titleMinHeight, ...darkTextStyle }}
				>
					{title}
				</div>

				{eventName ? (
					<div className={clsx('line-clamp-1 text-sm font-medium', isDark ? '' : 'text-gray-700')} title={eventName} style={darkTextStyle}>
						{eventName}
					</div>
				) : null}

				{subtitle || !hideEmptySubtitle ? (
					<div
						className={clsx('line-clamp-1 text-sm', isDark ? '' : 'text-gray-600')}
						style={{ minHeight: subtitleMinHeight, ...darkTextStyle }}
					>
						{subtitle}
					</div>
				) : null}

				{metaLines?.map((line) => (
					<div
						key={line.id}
						className={clsx('text-sm', {
							'flex items-start gap-2': line.icon,
							'line-clamp-1': !line.icon,
							'text-gray-700': !isDark
						})}
						style={{ minHeight: metaLineMinHeight, ...darkTextStyle }}
					>
						{line.icon ? (
							<>
								<span className="mt-0.5 shrink-0">{line.icon}</span>
								<div className="min-w-0 break-words">{line.content}</div>
							</>
						) : (
							line.content
						)}
					</div>
				))}
			</div>

			{hideBottomRow ? null : (
				<div className="mt-4 flex flex-row flex-wrap items-center justify-between gap-1" style={{ minHeight: footerMinHeight }}>
					{footerLeft ? (
						<p className="text-sm font-medium" style={footerLeftStyle}>
							{footerLeft}
						</p>
					) : (
						<span />
					)}
					{footerRight ? (
						<div
							className={clsx('text-xs flex items-center', isDark ? '' : 'text-gray-600')}
							style={{ minHeight: '1.75rem', ...darkTextStyle }}
						>
							{footerRight}
						</div>
					) : (
						<span />
					)}
				</div>
			)}
		</div>
	);
};
